import React, { useState } from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { Flex, Text } from "styles/utility-components";

import SrcDocsLink from "components/actions/SrcDocsLink";
import Button from "components/ui/button/Button";
import { formatDate } from "lib/utils";

export type SourceNotice = {
  id: string;
  noticeType?: string | null;
  publishDate?: string | null;
  url: string;
};

const SOURCE_NOTICES_SHOWN = 3;

/** When a pre-tender has had more than one notice, the newest one's publish date. */
export function getLatestSourceNoticeDate(
  sourceNotices: SourceNotice[] | null | undefined,
): string | undefined {
  // The API returns source notices newest first, and only for pre-tenders
  if (!sourceNotices || sourceNotices.length < 2) {
    return undefined;
  }
  return sourceNotices[0].publishDate ?? undefined;
}

const NOTICE_TYPE_LABELS: Record<string, string> = {
  UK1: "Pipeline notice",
  UK2: "Market engagement notice",
  UK3: "Planned procurement notice",
};

function describeSourceNotice({ noticeType, publishDate }: SourceNotice): string {
  const label = (noticeType && NOTICE_TYPE_LABELS[noticeType]) || "Notice";
  if (!publishDate) {
    return label;
  }
  // A calendar date, so read it in the viewer's zone to keep the same day
  return `${label} · ${formatDate(DateTime.fromISO(publishDate).toJSDate())}`;
}

/** Every notice published at the source for a pre-tender, newest first. */
export default function SourceNoticesProperty({
  sourceNotices,
}: {
  sourceNotices: SourceNotice[];
}) {
  const { colors } = useTheme();
  const [showAll, setShowAll] = useState(false);

  const shownNotices = showAll ? sourceNotices : sourceNotices.slice(0, SOURCE_NOTICES_SHOWN);
  const hiddenCount = sourceNotices.length - SOURCE_NOTICES_SHOWN;

  return (
    <Flex gap={8} alignItems="flex-start" aria-label="Source notices">
      <TitleContainer>
        <Text color={colors.sysTextSecondary}>Sources</Text>
      </TitleContainer>
      <NoticesContainer>
        {shownNotices.map((notice) => (
          <Flex key={notice.id} column>
            {/* The notice id, as the urls only differ at the end */}
            <SrcDocsLink url={notice.url} linkType="text" label={notice.id} showIcon />
            <Text color={colors.sysTextSecondary}>{describeSourceNotice(notice)}</Text>
          </Flex>
        ))}
        {hiddenCount > 0 && (
          <ShowAllContainer>
            <Button buttonType="tertiary" onClick={() => setShowAll(!showAll)}>
              {showAll ? "Show fewer notices" : `Show all ${sourceNotices.length} notices`}
            </Button>
          </ShowAllContainer>
        )}
      </NoticesContainer>
    </Flex>
  );
}

const TitleContainer = styled(Flex)({
  alignItems: "center",
  minWidth: "30%",
});

// Offsets the tertiary button's 12px padding and 1px border so its text lines up with the links
const ShowAllContainer = styled.div({
  marginLeft: -13,
});

const NoticesContainer = styled(Flex)({
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 12,
  minWidth: 0,
  maxWidth: "70%",
});
